import { Spinner } from '@vkontakte/vkui';
import { clsx } from 'clsx';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import styles from './ui.module.css';

/**
 * Кнопка Мезы.
 *
 * Заводится своя, а не настраивается вкуишная, по той же причине, что и
 * остальные примитивы: в Мезе кнопка — это КОНТУР со слабой заливкой и
 * свечением, а VKUI рисует сплошную плиту. Контур токенами не выдаётся:
 * `mode="secondary"` берёт фон из `--vkui--color_background_secondary_alpha`
 * и рамки не имеет вовсе — на тёмном фоне это rgba(0,0,0,0.04), то есть
 * ничего. Отсюда и жалоба «не везде уместны кнопки-текст»: половина действий
 * в приложении выглядела подсвеченной подписью, потому что фон был невидим.
 *
 * Тон говорит не про цвет, а про роль действия:
 *
 * - `action` — главный ход экрана и необратимое решение: отправить, принять,
 *   удалить данные, включить техработы. Малиновый с обводкой и свечением;
 * - `accent` — согласие рангом ниже: править, сохранить, заменить фото;
 * - `quiet` — отказ от действия и всё служебное: отмена, в архив, показать
 *   ещё, отклонить;
 * - `danger` — снос, стоящий рядом с другим действием: малиновый контур без
 *   заливки, чтобы не спорить с главной кнопкой;
 * - `add` — пунктир: «завести ещё», добавить метку;
 * - `more` — «показать ещё»: не действие над вещью, а продолжение списка, и в
 *   макетах у него свой контур — фиолетовый, из той же связки, что и линия
 *   раздела;
 * - `link` — подчёркнутая подпись без рамки. Единственное место, где кнопка
 *   и правда выглядит текстом: раскрыть снимок строки журнала.
 *
 * Размер `l` пишется прописными — но только у тонов, которыми принимают
 * решение (`action`, `accent`, `danger`). Рядом с «СОХРАНИТЬ» в макетах всегда
 * стоит «отмена» строчными и легче весом: уход с экрана не объявляют.
 * `keepCase` отменяет складывание регистра совсем — когда на кнопке стоит не
 * команда, а имя места («Реестр», «База внешек»): это имя собственное.
 */

export type ActionTone = 'action' | 'accent' | 'quiet' | 'danger' | 'add' | 'more' | 'link';

type OwnProps = {
  tone?: ActionTone;
  size?: 's' | 'm' | 'l';
  /** Растянуть по ширине родителя: кнопка в полосу или единственная на блок. */
  stretched?: boolean;
  loading?: boolean;
  keepCase?: boolean;
  /**
   * Только значок: подпись остаётся в разметке, но видимой не рисуется — её
   * читает скринридер и показывает всплывающая подсказка системы. Нужна там,
   * где в ряду стоят три-четыре равных действия: подписи у них длиннее самой
   * кнопки, ломаются на две строки и ряд перестаёт читаться.
   *
   * Подпись при этом обязательна и пишется как обычно: кнопка без имени не
   * существует ни для скринридера, ни для поиска по экрану.
   */
  iconOnly?: boolean;
  before?: ReactNode;
  children: ReactNode;
};

type ActionProps = OwnProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof OwnProps> & {
    /** Ссылка вместо кнопки (профиль игрока) или label для скрытого input. */
    Component?: 'button' | 'label' | 'a';
    href?: string;
    target?: string;
    rel?: string;
  };

const TONES: Record<ActionTone, string> = {
  action: styles.actionAction,
  accent: styles.actionAccent,
  quiet: styles.actionQuiet,
  danger: styles.actionDanger,
  add: styles.actionAdd,
  more: styles.actionMore,
  link: styles.actionLink,
};

const SIZES = {
  s: styles.actionS,
  m: styles.actionM,
  l: styles.actionL,
};

export function Action({
  tone = 'accent',
  size = 'm',
  stretched,
  loading,
  keepCase,
  iconOnly,
  disabled,
  before,
  children,
  className,
  Component,
  href,
  ...rest
}: ActionProps) {
  const Tag = (Component ?? (href === undefined ? 'button' : 'a')) as 'button';

  return (
    <Tag
      {...(Tag === 'button' ? { type: 'button' as const } : { href })}
      {...rest}
      disabled={Tag === 'button' ? disabled || loading : undefined}
      aria-disabled={Tag === 'button' ? undefined : disabled || loading}
      className={clsx(
        styles.action,
        TONES[tone],
        SIZES[size],
        stretched && styles.actionStretched,
        iconOnly && styles.actionIconOnly,
        keepCase && styles.actionKeepCase,
        (disabled || loading) && styles.actionDisabled,
        className,
      )}
    >
      {/* Спиннер ЗАМЕНЯЕТ подпись, а не встаёт рядом: рядом он сдвигал бы
          текст и менял ширину кнопки в момент нажатия. */}
      {loading ? <Spinner size="s" noColor /> : null}
      {loading ? null : before}
      {loading ? null : iconOnly ? (
        <span className={styles.visuallyHidden}>{children}</span>
      ) : (
        children
      )}
    </Tag>
  );
}
